import { deviceSession } from '@/privacy/deviceStorage';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { useSinglePageScroller } from './useSinglePageScroller';
import { RESTING_SPACER_SELECTOR, restingScrollTopOf } from './restingScrollTop';
import { DETAIL_PANEL_CLASS } from '@/features/work-items/detailPanelWidth';
import { RESTORING_POSITION_ATTRIBUTE } from '@/hooks/useScrollAnchor';
import { isStepNavigationState } from './stepNavigation';

export const STORAGE_PREFIX = 'featit:scroll-position:';

function storageKeyOf(pathname: string, search: string): string {
  return `${STORAGE_PREFIX}${pathname}${search}`;
}

/**
 * Whichever element is actually this screen's scroller — `document.scrollingElement` in the app's
 * own phone mode, `<main>` everywhere else (desktop, the iPad, and a browser at phone width, mobile
 * Safari included) — the same boundary `useSinglePageScroller` and `scrollScreenToTop` already draw.
 */
function scrollerElementOf(singleScroller: boolean): Element | null {
  if (singleScroller) return document.scrollingElement;
  return document.querySelector('main');
}

/**
 * I-1304 (IL-17): a scroller of its own inside the page, kept per address beside the page's. In the
 * desktop layout an item drawn beside its list is read in the detail panel, and the panel scrolls,
 * not `<main>`: Back from a link far down its description drew it at its top (1695 kept, 0 at rest,
 * the iPad app at 1180). One panel per page; the class every detail panel already carries
 * (`detailPanelWidth.ts`) is what marks it, and only while it is a scroller (a placeholder is not).
 */
/**
 * I-1357 (IL-19a): a detail pane that is not a resizable panel and so has no panel class — the
 * decision beside Decisions or Tasks for me (`WaitingSplit`) — is marked by this attribute instead.
 * A desktop browser now follows a link in its text in place, and Back drew it at its top (762 kept,
 * 0 at rest, 1280).
 */
export const KEPT_PANEL_ATTRIBUTE = 'data-kept-panel';
const PANEL_SELECTOR = `.${DETAIL_PANEL_CLASS}, [${KEPT_PANEL_ATTRIBUTE}]`;

function panelKeyOf(pathname: string, search: string): string {
  return `${storageKeyOf(pathname, search)}#panel`;
}

function isPanelScroller(target: EventTarget | null): target is Element {
  if (!(target instanceof Element) || !target.matches(PANEL_SELECTOR)) return false;
  const overflow = getComputedStyle(target).overflowY;
  return overflow === 'auto' || overflow === 'scroll';
}

/**
 * Drawn, not only mounted: a screen being left can stay in the document under `display: none` (a
 * Suspense boundary hides what it showed while the next screen's code loads), panel and all, and its
 * panel comes first in the document. Given the offset, it would take it and this page's never would.
 */
function drawn(element: Element): boolean {
  for (let at: Element | null = element; at; at = at.parentElement) {
    if (getComputedStyle(at).display === 'none') return false;
  }
  return true;
}

function panelScroller(): Element | null {
  for (const element of document.querySelectorAll(PANEL_SELECTOR)) {
    if (isPanelScroller(element) && drawn(element)) return element;
  }
  return null;
}

function readStoredOffset(key: string): number | null {
  try {
    const raw = deviceSession.getItem(key);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private mode, or storage disabled entirely: nothing to restore, not an error to surface.
    return null;
  }
}

function writeStoredOffset(key: string, top: number): void {
  try {
    deviceSession.setItem(key, String(Math.round(top)));
  } catch {
    // A full or blocked store loses the memory of one scroll position, nothing more.
  }
}

/**
 * How long a restore keeps re-asserting itself against a list that is still growing underneath it.
 * A browser clamps an assigned `scrollTop` to whatever the element's `scrollHeight` allows *right
 * now* — on a fresh mount (a reload, a pop into a list that was never on screen this session) the
 * rows have not necessarily arrived yet (the list's own data read is still in flight) or a
 * virtualized list's rows are still settling their measured heights, so the first assignment can
 * land short and then never correct itself, because nothing else re-applies `scrollTop` once
 * content grows beneath it. A reader who actually scrolls ends the chase at once (`onInteract`),
 * so this only ever costs real time against a list that is genuinely still loading.
 */
const RESTORE_TIMEOUT_MS = 2_000;

/**
 * Sets `scrollTop`, then keeps re-applying it — on every animation frame for `RESTORE_TIMEOUT_MS`,
 * and whenever the content grows for as long as it takes (I-1304) — until it is reached, the
 * element leaves the document, the reader moves the page themselves (a wheel, a touch, a pointer
 * down, a key — this is a restore, never a lock against a reader's own scroll), or the next
 * page is left: on another page it does nothing.
 * `onDone` fires exactly once-or-more when it ends, so the saver can tell a restore in flight (its
 * clamped, not-yet-final offsets are not the reader's position) from the reader's own scrolling.
 */
/**
 * I-1193 (simq-i1193, kp29f/kp30f): whether `top` is really reached — the offset reads back AND the
 * layout can hold it. WebKit in the app accepts a document `scrollTop` past what the page allows by
 * moving the visual viewport (523 read back on a document 874 tall, before the feed had a row),
 * then clamps it to 0 a frame later; taken at its word the restore ended there, the clamp's 0 was
 * stored as the reader's place, and the feed drew its rows at the top. An element with no layout at
 * all (`scrollHeight` 0) has nothing to clamp against, so its offset is taken as it reads.
 */
function reached(element: Element, top: number): boolean {
  if (element.scrollTop < top) return false;
  if (element.scrollHeight === 0) return true;
  return element.scrollHeight - element.clientHeight >= top;
}

/**
 * I-1274 (reopened): tells `onGrow` when the content of `scroller` changes size, after layout and
 * before that frame is painted. Going back, the address changes a commit before the list is drawn:
 * the offset is assigned against the screen being left, the browser clamps it (900 → 646), and the
 * list then arrived after that frame's animation callbacks had run — so its first frame was painted
 * at the clamp and the next at the offset (752 then 900 on dev, on every back after the first). A
 * `ResizeObserver` is told in the frame the list is laid out in, so the offset is there before it
 * is seen.
 *
 * The screen's content is `<main>` whichever element scrolls, and its children are what is watched,
 * new ones included: as the scroller `<main>` never changes size itself. The search field's spacer
 * is left out (`RESTING_SPACER_SELECTOR`).
 */
function watchContent(scroller: Element, onGrow: () => void): () => void {
  if (typeof ResizeObserver === 'undefined') return () => undefined;
  const content =
    scroller === document.scrollingElement ? document.querySelector('main') : scroller;
  if (!content) return () => undefined;
  const sizes = new ResizeObserver(onGrow);
  const watchChildren = () => {
    for (const child of Array.from(content.children)) {
      if (!child.matches(RESTING_SPACER_SELECTOR)) sizes.observe(child);
    }
  };
  const children = new MutationObserver(() => {
    watchChildren();
    onGrow();
  });
  children.observe(content, { childList: true });
  watchChildren();
  return () => {
    sizes.disconnect();
    children.disconnect();
  };
}

/**
 * @param offset the offset to give back, or how to read it: a screen put at its rest follows the
 *   rest while the restore is open (I-1296 reopened: the search field's tuck grows with the text
 *   size, and a restore holding the default size's 64 pulled the list back from 95, the field's
 *   lower 23 px showing under the top row).
 * @param stillHere whether the page the offset belongs to is still the one on screen. A restore
 *   has no deadline (I-1304), so it must not place a screen the reader was sent on to.
 */
function restoreScrollTop(
  element: Element,
  offset: number | (() => number),
  stillHere: () => boolean,
  onDone: () => void,
): void {
  const goal = typeof offset === 'number' ? () => offset : offset;
  const top = goal();
  element.scrollTop = top;
  if (top <= 0) {
    onDone();
    return;
  }
  const chaseUntil = Date.now() + RESTORE_TIMEOUT_MS;
  let stopped = false;
  /*
    I-1304 (IL-17): a pane that holds its reader's place itself (`useScrollAnchor`) stands down while
    this one is open. A detail panel's parts arrive above the offset as it is given back; the anchor
    held what the half-drawn panel showed and carried it 800 past the place (1695 kept, 2497 at rest,
    the iPad app at 1180).
  */
  element.setAttribute(RESTORING_POSITION_ATTRIBUTE, '');
  // I-1304 (IL-8): Chromium keeps what is on screen in place as content grows above it (scroll
  // anchoring). An item's page is assigned its offset while its description is still being drawn
  // again above the reader, and anchoring then carried the page on by what grew (kept 1516, at
  // rest 2456). The offset is the reader's, so while a restore is open it alone places the page.
  const html = element instanceof HTMLElement ? element : null;
  const anchoring = html?.style.getPropertyValue('overflow-anchor') ?? '';
  html?.style.setProperty('overflow-anchor', 'none');
  /*
    I-1513 (IL-8d, nightly 20261013T020028Z): and while its reach settles. Back drew an
    organisation's Activity with the work areas' sections above the feed still loading; the feed
    arrived, 920 was reached and the restore stopped, and the sections then took their drawn
    height, 48 over their placeholders. Anchored again by then, the page went on to 968, and the
    entry the reader had tapped came back 48 higher. Anchoring returns when nothing restores any more.
  */
  let anchored = false;
  const reanchor = () => {
    if (anchored) return;
    anchored = true;
    if (anchoring) html?.style.setProperty('overflow-anchor', anchoring);
    else html?.style.removeProperty('overflow-anchor');
  };
  /*
    The content grew: the offset goes back in the frame that draws it, not the one after.

    I-1304 (IL-8b): and for as long as it takes. The watch ended with the chase below, 2 s in, and
    a page drawn later than that stayed at its clamp — on the phone a long item came back about
    790 pt above the place left, by a different amount each run (the stand-in with the processor
    slowed 60 times: 1516 kept, 663 at rest). Content has no deadline: the restore is open until
    the offset is reached or the reader moves the page. While another page is on screen it does
    nothing; back on its own, it goes on.
  */
  /*
    I-1375 (the gate's WebKit, All work areas' Activity on a phone): reached is not settled while the
    chase's time runs. Back drew the sections over a feed still loading: 200 was reached at once on
    a page 1082 tall, a section's chart then took the place of its taller placeholder (954, the
    offset clamped to 110), and the feed arrived to a restore already over. Until the chase's time is
    up the watch stays, and puts back an offset a shrink took; the reader moving the page, or the
    page being left, ends it.

    I-1456 (nightly 20261012T014439Z, IL-8b's decision with the processor slowed 60 times): and a
    reach after the chase's time settles too. The decision was reached at 807 on a page drawn in part
    (1658 tall), 5 s after the pop; a second later the page shrank to 1548 and the browser clamped
    the offset to 704, and the rest of the page (2297) arrived to a restore already over. A reach is
    settled once the page has held it for the chase's length, counted from the reach; a shrink
    before then opens the restore again, and it stays open until the offset is held once more.
  */
  let settling = false;
  let settleUntil = chaseUntil;
  let settleDeadline: ReturnType<typeof setTimeout> | undefined;
  const unwatch = watchContent(element, () => {
    // Settling ends once the offset has been held for its time, or with the page: back on it later,
    // another restore owns it, and this one's offset is no longer the reader's.
    if (settling && (!stillHere() || (Date.now() > settleUntil && reached(element, goal())))) {
      settling = false;
      reanchor();
      unwatch();
      for (const type of READER_MOVES) window.removeEventListener(type, readerMoved);
      return;
    }
    if ((stopped && !settling) || !element.isConnected || !stillHere()) return;
    if (!reached(element, goal())) element.scrollTop = goal();
    // While the chase runs it is the chase that ends the restore, a frame later: a list drawn again
    // tucks its search field in the same task, and that must not be the last word (64 for 900).
    if (Date.now() > chaseUntil && reached(element, goal())) stop(true);
  });
  const stop = (settle = false) => {
    if (stopped) return;
    stopped = true;
    element.removeAttribute(RESTORING_POSITION_ATTRIBUTE);
    onDone();
    settling = settle && element.isConnected;
    settleUntil = Math.max(chaseUntil, Date.now() + RESTORE_TIMEOUT_MS);
    if (!settling) {
      reanchor();
      unwatch();
      for (const type of READER_MOVES) window.removeEventListener(type, readerMoved);
      return;
    }
    /*
      I-1513 (IL-8d): the settling is ended by the content changing, and a page that has stopped
      changing never tells it. So its time is a deadline too: anchoring is back when it is up, and a
      reach still held then (or a page since left) is settled without waiting for one more change.
      The scroller is never left unanchored past it.
    */
    clearTimeout(settleDeadline);
    settleDeadline = setTimeout(
      () => {
        reanchor();
        if (!settling || (stillHere() && !reached(element, goal()))) return;
        settling = false;
        unwatch();
        for (const type of READER_MOVES) window.removeEventListener(type, readerMoved);
      },
      settleUntil - Date.now() + 1,
    );
  };
  const readerMoved = () => {
    settling = false;
    stop();
    reanchor();
    unwatch();
    for (const type of READER_MOVES) window.removeEventListener(type, readerMoved);
  };
  // On the window: a key that scrolls is not sent to the scroller, and every touch passes here.
  for (const type of READER_MOVES) {
    window.addEventListener(type, readerMoved, { passive: true, once: true });
  }
  const tick = () => {
    if (stopped) return;
    if (!element.isConnected || (stillHere() && reached(element, goal()))) {
      stop(element.isConnected);
      return;
    }
    // The chase is per frame, so it is bounded; what arrives later is the watch's.
    if (Date.now() > chaseUntil) return;
    if (stillHere()) element.scrollTop = goal();
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** What ends a restore: the reader moving the page themselves. Never a lock against their scroll. */
const READER_MOVES = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * IU-14 (`docs/concept-iphone-ux.md#iu-14-keeping-the-position`, F-0503): the list keeps its place
 * across a pop, a reload and a relaunch. Mounted once in `Layout.tsx`, above the `<Outlet>`, so it
 * sees every route the reader visits rather than one page wiring its own effect.
 *
 * ⛔ **Not `<main>`'s own scroll position alone** — this slice's own named mutation. `<main>` is one
 * shared DOM node across every route (`Layout.tsx` mounts it once; only the `<Outlet>` inside it
 * swaps), so left to itself its `scrollTop` is whatever the *previous* screen happened to leave it
 * at, not this path's own. A pop from a detail view scrolled 300 back to a list left at 900 landed
 * at 300 pt off, on another row entirely, because nothing told `<main>` which number belonged to
 * which path. This hook stores one offset per path in `sessionStorage` (so it survives a reload or a
 * relaunch, not only an in-session pop) and reads `useSinglePageScroller()` itself for which element
 * that offset actually lives on, rather than assuming `<main>` either way.
 *
 * `useNavigationType()` is the one way to tell a *pop* (back/forward) from a push: a push is a new
 * screen and starts at its own top, the ordinary web behaviour a reader expects from opening
 * something new. A pop — and a reload or a relaunch, which looks identical to the first screen this
 * hook ever sees: there is no earlier "push" to compare it with, so it is read the same way — gets
 * the stored offset back, within the rounding `sessionStorage` already costs it.
 */
/**
 * I-1193: the app keeps the position itself, so the browser must not. Left at `auto`, WebKit put
 * the document back on a pop by its own record, against a feed that had not loaded yet: clamped to
 * 0, after this hook's restore had already landed, and the Activity feed came back at its top in
 * the app (simq-t0566b; T-0567's spec had already seen it and only turned it off in the test).
 */
export function takeScrollRestorationFromTheBrowser(): void {
  if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
}

export function useKeepPosition(): void {
  takeScrollRestorationFromTheBrowser();
  const location = useLocation();
  const navigationType = useNavigationType();
  const singleScroller = useSinglePageScroller();
  const pathKey = `${location.pathname}${location.search}`;
  // I-1274: a step by ⌃ ⌄ replaces the entry, and opens the item at its top as a push does.
  const arrivedAsPush =
    navigationType === NavigationType.Push || isStepNavigationState(location.state);

  // Keyed on `pathKey` and nothing finer: a reader action within the same path (resolving a row,
  // a reply landing) must never retrigger this — that is the "action that changes the list keeps
  // the rows in place" half of IU-14, and it falls out for free by not depending on anything that
  // changes for that reason.
  //
  // `useLayoutEffect`, not `useEffect`: the restore has to land before the browser paints the new
  // screen at 0, or the reader sees the jump from the top down to row 900 rather than nothing at
  // all — the same reason `scrollScreenToTop` and the rest of IU-15's motion work land before paint.
  const currentKeyRef = useRef(pathKey);
  const restoringRef = useRef(false);
  // Which restore holds `restoringRef`: the latest one started, whose end clears it.
  const restoreRef = useRef(0);
  const currentPathnameRef = useRef(location.pathname);
  const singleScrollerRef = useRef(singleScroller);
  singleScrollerRef.current = singleScroller;
  const shownRef = useRef(false);
  useLayoutEffect(() => {
    // Before anything is scrolled: the previous path's listener is still attached until the passive
    // cleanup, and the reset below fires a scroll event it would otherwise store under ITS key —
    // overwriting the offset a pop is about to need (measured: the list's 900 became 0).
    currentKeyRef.current = pathKey;
    /*
      A list rewriting its own query (the reader's search, a filter: a replace that keeps the page)
      is the same screen, so it stays where the reader has it. Placed at its rest (I-1296), the
      Items search field the reader was typing in was tucked behind the top row as the first
      letters wrote `?q=` (gate 524, `text-size-narrow-chrome.spec.ts`). A restore still under way
      for the page carries on through it: its own check is by the page, not the query.
    */
    const rewroteOwnQuery =
      shownRef.current &&
      navigationType === NavigationType.Replace &&
      !isStepNavigationState(location.state) &&
      currentPathnameRef.current === location.pathname;
    shownRef.current = true;
    if (rewroteOwnQuery) return;
    const element = scrollerElementOf(singleScroller);
    if (!element) return;
    const stored = arrivedAsPush
      ? null
      : readStoredOffset(storageKeyOf(location.pathname, location.search));
    const pathname = location.pathname;
    currentPathnameRef.current = pathname;
    restoringRef.current = true;
    const restore = ++restoreRef.current;
    /*
      I-1296 (the device: Items opened by the shell rested with its search field showing): a new
      screen starts where it rests, not at 0. The search field names its tuck on the scroller in
      its own layout effect, which runs before this one; reset to 0 after it, the list showed its
      field and stored 0, and every later visit came back to 0.
    */
    restoreScrollTop(
      element,
      stored ?? (() => restingScrollTopOf(element)),
      // By the page, not its query: a list rewrites its own query as it settles.
      () => currentPathnameRef.current === pathname,
      () => {
        if (restoreRef.current !== restore) return;
        restoringRef.current = false;
        if (currentKeyRef.current !== pathKey) return;
        // A screen put at its rest is kept there, as a reader who left it there would have it: a
        // pop then returns to it even if its search field has not named its tuck by then.
        const rest = stored === null ? restingScrollTopOf(element) : 0;
        if (rest > 0 && Math.abs(element.scrollTop - rest) < 1) {
          writeStoredOffset(storageKeyOf(location.pathname, location.search), rest);
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey]);

  /*
    I-1193 (back before the item draws): a pop back to the path on screen restores it too. A tap that
    opens an item and a Back that comes before the item has drawn settle in one render, so the path
    on screen never changes and the effect above never runs — while the list had already been taken
    off the screen under the item and fallen to 0. The offset was stored as the reader scrolled, so
    the pop restores it whether or not the item ever drew. A frame later, so the render the pop
    causes has committed first.
  */
  useEffect(() => {
    const onPop = () => {
      // Read now, before the pop's render: a list drawn again scrolls itself (the phone's search
      // field hides under the top row, I-1204), and that write is stored under this same path.
      const key = `${window.location.pathname}${window.location.search}`;
      const stored = readStoredOffset(
        storageKeyOf(window.location.pathname, window.location.search),
      );
      requestAnimationFrame(() => {
        if (stored === null || key !== currentKeyRef.current) return;
        const element = scrollerElementOf(singleScrollerRef.current);
        if (!element || Math.abs(element.scrollTop - stored) < 1) return;
        const pathname = currentPathnameRef.current;
        restoringRef.current = true;
        const restore = ++restoreRef.current;
        restoreScrollTop(
          element,
          stored,
          () => currentPathnameRef.current === pathname,
          () => {
            if (restoreRef.current === restore) restoringRef.current = false;
          },
        );
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Saved continuously while scrolling, not only when the reader leaves: a reload can land at any
  // offset, and an effect that waited for unmount would read one render too late for that case.
  // Coalesced to once per animation frame (`pending`) — the native `scroll` event can fire many
  // times inside one frame, and a `sessionStorage` write on every one of them is exactly the kind
  // of per-frame cost IU-14's own p95 budget (≤ 20 ms) would catch.
  useEffect(() => {
    const element = scrollerElementOf(singleScrollerRef.current);
    if (!element) return;
    const key = storageKeyOf(location.pathname, location.search);
    let pending = false;
    /*
      The reader's last offset here, kept as each scroll is heard. A tap right after a scroll can
      change the path before the frame the write waits for: that frame then belongs to the next
      screen and skips the write, and the pop came back at the offset stored before (T-0552: 6 of
      20 pops at the top in a busy WebKit, where frames come late). Leaving the path writes it.
    */
    let readerTop: number | null = null;
    let lastTop = element.scrollTop;
    let lastHeight = element.scrollHeight;
    /** The page's height before its content was taken away under the reader, while it is away. */
    let emptiedFrom: number | null = null;
    /*
      I-1278: the address this screen was drawn for. A tap moves the address at once and the screen a
      render later — longer while the next screen's code loads — and in between the list's rows go
      and the next screen's arrive in one frame: the browser's clamp to 0 is reported with a page
      taller than before, so it is no shrink the guard below can see, and 0 was stored over the
      list's 64 (the shell, every back after the first). Once the address has left, nothing that
      scrolls here is the reader's place on this screen.
    */
    const addressOf = () => `${window.location.pathname}${window.location.search}`;
    const drawnFor = addressOf();
    const addressLeft = () => addressOf() !== drawnFor;
    const readerTouched = () => {
      emptiedFrom = null;
    };
    // I-1193 (measured, and not a product fault): a feed once read back at 0 after Back in a browser
    // at 390. The 0 was Playwright's `click()` scrolling a half-hidden row into view before it
    // clicked — traced frame by frame in WebKit, no app code wrote it. A real tap leaves `<main>` where
    // it was until the item draws, and this hook keeps the feed's place; nothing here guards for it.
    const onScroll = () => {
      const top = element.scrollTop;
      const height = element.scrollHeight;
      // The content shrank and the browser clamped the offset with it — the screen being left
      // unmounts its rows a render before this hook sees the new path (measured: the list's 900 was
      // stored as 0 when Open replaced the list with the detail). That is the content leaving, not
      // the reader scrolling up, so it is never the position to keep.
      const clampedByShrink = height < lastHeight && top < lastTop;
      /*
        I-1274: and nothing that scrolls the emptied screen afterwards is the reader's either. With
        the rows gone the phone's search field tucks itself again (64), a frame after the clamp and
        still under this path: stored, it replaced the list's 900 and Back returned to 64. Until the
        page is as tall as it was, or the reader touches it, the place kept is the one before.
      */
      if (clampedByShrink) emptiedFrom = Math.max(emptiedFrom ?? 0, lastHeight);
      else if (emptiedFrom !== null && height >= emptiedFrom) emptiedFrom = null;
      lastTop = top;
      lastHeight = height;
      // A restore in flight scrolls to whatever the still-loading list allows; that is not the
      // reader's position, and storing it would shrink the very offset being restored.
      if (
        clampedByShrink ||
        emptiedFrom !== null ||
        addressLeft() ||
        restoringRef.current ||
        currentKeyRef.current !== pathKey
      ) {
        return;
      }
      readerTop = top;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        if (!pending) return;
        pending = false;
        if (addressLeft() || restoringRef.current || currentKeyRef.current !== pathKey) return;
        writeStoredOffset(key, element.scrollTop);
      });
    };
    // A reload or a relaunch can land inside the frame the write above is still waiting for, and
    // would restore the offset one scroll too old; the page leaving flushes the current one now.
    const flush = () => {
      if (
        emptiedFrom !== null ||
        addressLeft() ||
        restoringRef.current ||
        currentKeyRef.current !== pathKey
      ) {
        return;
      }
      writeStoredOffset(key, element.scrollTop);
    };
    // The document's scroll is reported at the document, never at its root element: listened for on
    // `<html>`, nothing was ever stored in the app, and a pop returned the Activity feed to its top
    // (T-0565, measured: no offset saved however far the feed was scrolled).
    const scrollEvents: EventTarget = element === document.scrollingElement ? document : element;
    scrollEvents.addEventListener('scroll', onScroll, { passive: true });
    const touches = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    for (const type of touches) window.addEventListener(type, readerTouched, { passive: true });
    window.addEventListener('pagehide', flush);
    return () => {
      scrollEvents.removeEventListener('scroll', onScroll);
      for (const type of touches) window.removeEventListener(type, readerTouched);
      window.removeEventListener('pagehide', flush);
      if (pending && readerTop !== null) writeStoredOffset(key, readerTop);
      pending = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey, singleScroller]);

  /*
    I-1304 (IL-17): the detail panel's own offset, by the same rules as the page's: stored per
    address as the reader scrolls it, never while its address has left or a restore is placing it,
    and given back on a pop, a reload or a relaunch, never on a push. The panel is drawn after the
    page (its list's code and its item's read come first), so the restore waits for it to be there
    and then places it as the page is placed: through every late-drawn part, until the offset is
    reached or the reader moves it, and nothing while another address is on screen.
  */
  useEffect(() => {
    const key = panelKeyOf(location.pathname, location.search);
    const pathname = location.pathname;
    const addressOf = () => `${window.location.pathname}${window.location.search}`;
    const drawnFor = addressOf();
    // By the router's page, as the page's own restore reads it.
    const stillHere = () => currentPathnameRef.current === pathname;
    let restoring = false;
    let ended = false;
    let readerTop: number | null = null;
    let pending = false;

    const onScroll = (event: Event) => {
      const panel = event.target;
      if (!isPanelScroller(panel) || restoring || addressOf() !== drawnFor) return;
      readerTop = panel.scrollTop;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        if (restoring || addressOf() !== drawnFor || !panel.isConnected) return;
        writeStoredOffset(key, panel.scrollTop);
      });
    };
    // A scroll does not bubble, so it is heard on its way down.
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    /*
      I-1612 (NX-8): a push or a step opens the panel's new item at its top. The panel is one element
      across the items drawn in it, so left alone it kept the last one's offset: Next from a decision
      answered at the foot of its pane opened the next one at its answer form, its title, key and
      queue position above the fold (650 kept, 1440). A query the list rewrites itself is a replace
      that is not a step, and keeps the panel where it is.
    */
    if (arrivedAsPush) {
      const panel = panelScroller();
      if (panel) panel.scrollTop = 0;
    }

    let unwatch = () => undefined as void;
    const stored = arrivedAsPush ? null : readStoredOffset(key);
    if (stored !== null && stored > 0) {
      const begin = (panel: Element) => {
        restoring = true;
        restoreScrollTop(panel, stored, stillHere, () => {
          restoring = false;
        });
      };
      const present = panelScroller();
      if (present) begin(present);
      else {
        const arrival = new MutationObserver(() => {
          if (ended || !stillHere()) return;
          const panel = panelScroller();
          if (!panel) return;
          arrival.disconnect();
          begin(panel);
        });
        arrival.observe(document.body, {
          childList: true,
          subtree: true,
          // A hidden screen shown again changes its style, not its children.
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
        // The reader who moves on before it arrives keeps the panel where it is drawn.
        const stop = () => arrival.disconnect();
        for (const type of READER_MOVES) window.addEventListener(type, stop, { once: true });
        unwatch = () => {
          arrival.disconnect();
          for (const type of READER_MOVES) window.removeEventListener(type, stop);
        };
      }
    }
    return () => {
      ended = true;
      unwatch();
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (pending && readerTop !== null && !restoring) writeStoredOffset(key, readerTop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey]);
}
